import React, { useState, useEffect } from "react";
import axios from "axios";
import Intasend from "../payments/Intasend";
import { FHOST } from "../constants/Functions";
import { XMarkIcon } from '@heroicons/react/24/outline';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const MyWallet = ({ userInfo }) => {
  const [showPopup, setShowPopup] = useState(false);
  const [topupHistory, setTopupHistory] = useState([]);
  const [amount, setAmount] = useState(0);

  const chartData = {
    labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'],
    datasets: [
      {
        label: 'Wallet Activity',
        data: [5000, 15000, 8000, 12000, 10000, 18000],
        borderColor: '#015575',
        tension: 0.4,
        fill: false,
        pointBackgroundColor: '#fff',
        pointBorderColor: '#015575',
      },
    ],
  };

  const fetchTransactionHistory = async () => {
    try {
      const response = await axios.get(
        `${FHOST}/payments/api/transaction-history/${userInfo?.id}`
      );
      const transactions = response.data.transactions;
      const amount = response.data.amount;
      setAmount(amount);
      const deposits = transactions.filter(
        (transaction) => transaction.transaction_type === "deposit"
      );
      setTopupHistory(deposits);
    } catch (error) {
      console.error("Error fetching transaction history:", error);
    }
  };

  useEffect(() => {
    fetchTransactionHistory();
  }, [userInfo?.id]);

  return (
    <div className="min-h-screen bg-gray-50 font-josefin">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <h1 className="text-3xl font-lilita text-[#015575] text-center mb-8">
          My Wallet
        </h1>

        {/* Wallet Summary Grid */}
        <div className="grid gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
          {/* Total Balance Card */}
          <div className="bg-white p-6 rounded-2xl shadow-sm border-l-4 border-[#015575]">
            <h2 className="text-gray-500 text-sm">Total Balance</h2>
            <p className="text-3xl font-bold text-[#015575] mt-2">
              Ksh {amount.toLocaleString()}
            </p>
            <p className="text-gray-500 text-sm mt-2">Available funds</p>
          </div>

          {/* Add Funds Card */}
          <div className="bg-white p-6 rounded-2xl shadow-sm">
            <h2 className="text-gray-500 text-sm">Add Funds</h2>
            <button 
              onClick={() => setShowPopup(true)}
              className="mt-4 w-full bg-[#015575] text-white py-3 rounded-xl hover:bg-[#01415e] transition flex items-center justify-center"
            >
              <span className="text-sm">Fund Wallet</span>
            </button>
          </div>

          {/* Withdraw Card */}
          <div className="bg-white p-6 rounded-2xl shadow-sm">
            <h2 className="text-gray-500 text-sm">Withdraw Funds</h2>
            <button className="mt-4 w-full bg-gray-100 text-[#015575] py-3 rounded-xl hover:bg-gray-200 transition flex items-center justify-center">
              <span className="text-sm">Initiate Withdrawal</span>
            </button>
          </div>

          {/* Recent Topups Card */}
          <div className="bg-white p-6 rounded-2xl shadow-sm">
            <h2 className="text-gray-500 text-sm">Recent Topups</h2>
            <p className="text-3xl font-bold text-[#015575] mt-2">
              {topupHistory.length}
            </p>
            <p className="text-gray-500 text-sm mt-2">This month</p>
          </div>
        </div>

        {/* Chart Section */}
        <div className="mt-8 bg-white p-6 rounded-2xl shadow-sm">
          <h3 className="text-xl font-semibold text-[#015575] mb-4">Wallet Activity</h3>
          <div className="h-80">
            <Line 
              data={chartData} 
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: { position: 'top' },
                },
                scales: {
                  y: {
                    grid: { color: '#e5e7eb' },
                    ticks: { callback: value => `Ksh ${value}` }
                  },
                  x: { grid: { display: false } }
                }
              }} 
            />
          </div>
        </div>

        {/* Transaction History Section */}
        <div className="mt-8 grid gap-6 grid-cols-1 lg:grid-cols-2">
          {/* Deposit History */}
          <div className="bg-white p-6 rounded-2xl shadow-sm">
            <h3 className="text-xl font-semibold text-[#015575] mb-4">Deposit History</h3>
            <div className="space-y-4">
              {topupHistory.map((transaction, index) => (
                <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                  <div>
                    <p className="font-medium text-sm">
                      {new Date(transaction.timestamp).toLocaleDateString()}
                    </p>
                    <p className="text-xs text-gray-500">Deposit</p>
                  </div>
                  <span className="text-green-600 font-semibold">
                    +Ksh {transaction.amount.toLocaleString()}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Withdrawal History */}
          <div className="bg-white p-6 rounded-2xl shadow-sm">
            <h3 className="text-xl font-semibold text-[#015575] mb-4">Withdrawal History</h3>
            <div className="space-y-4">
              {[].map((transaction, index) => ( // Replace with actual withdrawal data
                <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                  <div>
                    <p className="font-medium text-sm">Mar 15, 2024</p>
                    <p className="text-xs text-gray-500">Withdrawal</p>
                  </div>
                  <span className="text-red-600 font-semibold">
                    -Ksh 5,000
                  </span>
                </div>
              ))}
              <p className="text-gray-500 text-center">No recent withdrawals</p>
            </div>
          </div>
        </div>

        {/* Add Funds Modal */}
        {showPopup && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-2xl p-6 w-full max-w-md">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-semibold text-[#015575]">Add Funds</h3>
                <button 
                  onClick={() => setShowPopup(false)}
                  className="p-2 text-gray-500 hover:text-gray-700"
                >
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>
              <Intasend 
                fetchTransactionHistory={fetchTransactionHistory} 
                userInfo={userInfo}
                onClose={() => setShowPopup(false)}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MyWallet;